import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { io } from 'socket.io-client';
import { updateFromWebSocket } from '../store/walletSlice';
import { updateFromWebSocket as updateProgressFromWebSocket } from '../../spin/store/progressSlice';
import { updateFromWebSocket as updateCountersFromWebSocket, updateSingleCounter } from '../../../store/slices/countersSlice';
import { updateFromWebSocket as updateTicketsFromWebSocket } from '../../tickets/store/ticketsSlice';
import {
  setTonPaymentFailed,
  setTonPaymentSuccess,
} from '../../../store/slices/tonPaymentSlice';

export const useWebSocket = (userId) => {
  const dispatch = useDispatch();
  const [state, setState] = useState({
    isConnected: false,
    lastUpdate: null,
  });
  const [socketState, setSocketState] = useState(null);

  const socketRef = useRef(null);

  const connect = useCallback(async () => {

    let token = localStorage.getItem('accessToken');
    if (!token) {
      console.warn('❌ No access token found for WebSocket connection');
      return;
    }

    // Проверяем, не истек ли токен
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);

      if (payload.exp && payload.exp < now) {
        console.warn('⚠️ Access token expired, attempting to refresh...');

        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          try {
            const { authAPI } = await import('../../../services/api');
            const newTokens = await authAPI.refreshToken(refreshToken);
            localStorage.setItem('accessToken', newTokens.accessToken);
            token = newTokens.accessToken;
            console.log('✅ Token refreshed successfully');
          } catch (refreshErr) {
            console.error('❌ Failed to refresh token:', refreshErr);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            return;
          }
        } else {
          console.warn('❌ No refresh token available');
          localStorage.removeItem('accessToken');
          return;
        }
      }
    } catch (error) {
      console.error('❌ Error checking token validity:', error);
      return;
    }

    if (!process.env.REACT_APP_WS_URL) {
      console.warn('⚠️ REACT_APP_WS_URL is not defined. WebSocket connection disabled.');
      return;
    }

    console.log('🔌 Connecting to WebSocket:', process.env.REACT_APP_WS_URL);

    const socket = io(process.env.REACT_APP_WS_URL, {
      query: { token },
      transports: ['websocket'],
      autoConnect: true,
      forceNew: true,
      upgrade: false,
      rememberUpgrade: false,
    });

    socket.on('connect', () => {
      setState(prev => ({ ...prev, isConnected: true }));
    });

    socket.on('disconnect', (reason) => {
      setState(prev => ({ ...prev, isConnected: false }));
    });

    socket.on('connected', (data) => {

      // Запрашиваем текущий баланс и прогресс при подключении
      socket.emit('getWalletBalance');

      socket.emit('getUserProgress');

      socket.emit('counters.request');
    });

    socket.on('wallet.balance.updated', (data) => {
      console.log('📥 Received wallet.balance.updated:', data);
      const currentUserId = userId || localStorage.getItem('userId');
      console.log('🔍 Comparing userIds:', { received: data.userId, current: currentUserId });

      if (data.userId === currentUserId) {
        console.log('✅ UserId matches, updating balance');
        const normalized = {
          ...data.data,
        };
        dispatch(updateFromWebSocket(normalized));
        setState(prev => ({ ...prev, lastUpdate: new Date() }));
      } else {
        console.warn('⚠️ UserId mismatch, ignoring balance update', { received: data.userId, current: currentUserId });
      }
    });

    socket.on('user.progress.updated', (data) => {

      const currentUserId = userId || localStorage.getItem('userId');

      if (data.userId === currentUserId) {
        const normalized = {
          ...data.data,
        };
        dispatch(updateProgressFromWebSocket(normalized));
        setState(prev => ({ ...prev, lastUpdate: new Date() }));
      } else {
      }
    });

    // Обработчики для счетчиков
    socket.on('counters.updated', (data) => {

      const currentUserId = userId || localStorage.getItem('userId');

      if (data.userId === currentUserId) {
        const normalized = {
          ...data.data,
          tickets: data.data?.tickets ? {
            ...data.data.tickets,
            nextAvailableTime: data.data.tickets.nextAvailableTime
              ? new Date(data.data.tickets.nextAvailableTime).toISOString()
              : null,
          } : undefined,
        };
        dispatch(updateCountersFromWebSocket(normalized));
        setState(prev => ({ ...prev, lastUpdate: new Date() }));
      } else {
      }
    });

    socket.on('counter.updated', (data) => {

      const currentUserId = userId || localStorage.getItem('userId');

      if (data.userId === currentUserId) {
        dispatch(updateSingleCounter({ type: data.data.type, count: data.data.count }));
        setState(prev => ({ ...prev, lastUpdate: new Date() }));
      } else {
      }
    });

    // Обработчик для обновления статуса тикетов
    socket.on('ticket.status.updated', (data) => {
      console.log('🎫 [WebSocket] ticket.status.updated received:', data);

      const currentUserId = userId || localStorage.getItem('userId');

      if (data.userId === currentUserId) {
        console.log('✅ [WebSocket] Updating tickets status from WebSocket');
        const normalized = {
          ...data.data,
          nextAvailableTime: data.data?.nextAvailableTime
            ? new Date(data.data.nextAvailableTime).toISOString()
            : null,
        };
        dispatch(updateTicketsFromWebSocket(normalized));
        setState(prev => ({ ...prev, lastUpdate: new Date() }));
      } else {
        console.log('⚠️ [WebSocket] Ticket update for different user, ignoring');
      }
    });

    socket.on('payments.ton.success', (data) => {
      const currentUserId = userId || localStorage.getItem('userId');

      if (data?.userId && data.userId !== currentUserId) {
        return;
      }

      dispatch(
        setTonPaymentSuccess({
          purchaseId: data?.purchaseId,
        }),
      );
    });

    socket.on('payments.ton.failed', (data) => {
      const currentUserId = userId || localStorage.getItem('userId');

      if (data?.userId && data.userId !== currentUserId) {
        return;
      }

      dispatch(
        setTonPaymentFailed({
          purchaseId: data?.purchaseId,
          error: data?.error,
        }),
      );
    });

    socket.on('ad.reward.granted', (data) => {
      console.log('🎯 [WebSocket] ad.reward.granted:', data);
      window.dispatchEvent(new CustomEvent('adRewardGranted', { detail: data.data || data }));
    });

    socket.on('pong', (data) => {
    });

    socket.on('error', (error) => {
      console.error('❌ WebSocket error:', error);
    });

    socketRef.current = socket;
    setSocketState(socket); // Обновляем состояние для синхронизации
  }, [userId, dispatch]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocketState(null); // Обновляем состояние
    }
  }, []);

  const ping = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('ping');
    } else {
    }
  }, []);

  useEffect(() => {
    // Подключаемся только если есть userId (пользователь залогинен)
    if (userId) {
      console.log('🔌 WebSocket: User logged in, connecting...', userId);
      connect();
    } else {
      console.log('🔌 WebSocket: No user ID, skipping connection');
    }

    return () => {
      disconnect();
    };
  }, [userId, connect, disconnect]);

  return {
    isConnected: state.isConnected,
    lastUpdate: state.lastUpdate,
    socket: socketState || socketRef.current, // Используем состояние, если доступно, иначе ref
    ping,
    reconnect: connect,
  };
};
